import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { API_BASE } from '../../lib/apiBase';
import {
  Mail,
  ArrowLeft,
  Save,
  RotateCcw,
  Send,
  Eye,
  Code,
  Copy,
  ChevronRight,
  Loader2,
  CheckCircle,
  FileText,
  Variable,
  Type,
} from 'lucide-react';

const API = API_BASE;

/* ------------------------------------------------------------------ */
/*  Small reusable bits                                                */
/* ------------------------------------------------------------------ */

const TypeIcon = ({ typeKey }) => {
  const map = {
    production_confirmed: '🎵',
    delivery_notice: '📦',
    upload_link: '📎',
    repro_email: '🎨',
    artwork_revision_requested: '✏️',
    artwork_ready_for_approval: '✅',
    partner_invitation: '🤝',
    password_reset: '🔑',
    invoice_email: '🧾',
  };
  return <span className="text-lg">{map[typeKey] || '📧'}</span>;
};

/* ------------------------------------------------------------------ */
/*  Template List                                                      */
/* ------------------------------------------------------------------ */

function TemplateList({ templates, onSelect }) {
  return (
    <div className="grid gap-3">
      {templates.map((t) => (
        <button
          key={t.type_key}
          data-testid={`template-item-${t.type_key}`}
          onClick={() => onSelect(t)}
          className="w-full text-left group"
        >
          <Card className="transition-all hover:shadow-md hover:border-slate-300">
            <CardContent className="flex items-center gap-4 py-4 px-5">
              <TypeIcon typeKey={t.type_key} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-slate-900 truncate">{t.label}</p>
                <p className="text-xs text-slate-500 truncate">{t.description}</p>
              </div>
              <div className="flex items-center gap-2">
                {t.is_default ? (
                  <Badge variant="outline" className="text-xs bg-slate-50 text-slate-500 border-slate-200">Default</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">Customised</Badge>
                )}
                <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
              </div>
            </CardContent>
          </Card>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Variable Chip                                                      */
/* ------------------------------------------------------------------ */

function VariableChip({ variable, onInsert }) {
  return (
    <button
      type="button"
      onClick={() => onInsert(variable.key)}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-mono
                 bg-violet-50 text-violet-700 border border-violet-200
                 hover:bg-violet-100 transition-colors cursor-pointer"
      title={`Sample: ${variable.sample}`}
    >
      <Copy className="h-3 w-3" />
      {`{{${variable.key}}}`}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Template Editor                                                    */
/* ------------------------------------------------------------------ */

function TemplateEditor({ template, onBack, token }) {
  const [form, setForm] = useState({
    subject: template.subject || '',
    preview_text: template.preview_text || '',
    heading: template.heading || '',
    body_html: template.body_html || '',
    cta_text: template.cta_text || '',
    cta_url_variable: template.cta_url_variable || '',
    footer_content: template.footer_content || '',
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showTestSend, setShowTestSend] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const quillRef = useRef(null);

  const variables = template.variables || [];
  const ctaVariables = variables.filter(v => v.key.includes('url') || v.key.includes('link'));

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  }), []);

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'align', 'link', 'image',
  ];

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const insertVariableIntoEditor = useCallback((key) => {
    const tag = `{{${key}}}`;
    if (!htmlMode && quillRef.current) {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection(true);
      editor.insertText(range.index, tag);
      editor.setSelection(range.index + tag.length);
      // Sync the value
      update('body_html', editor.root.innerHTML);
      toast.success(`Inserted ${tag}`);
    } else {
      navigator.clipboard.writeText(tag).then(() => {
        toast.success(`Copied ${tag} to clipboard`);
      }).catch(() => {
        toast.info(`Variable: ${tag}`);
      });
    }
  }, [htmlMode]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/admin/email-templates/${template.type_key}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template saved');
      setDirty(false);
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/email-templates/${template.type_key}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const def = res.data.template;
      setForm({
        subject: def.subject || '',
        preview_text: def.preview_text || '',
        heading: def.heading || '',
        body_html: def.body_html || '',
        cta_text: def.cta_text || '',
        cta_url_variable: def.cta_url_variable || '',
        footer_content: def.footer_content || '',
      });
      setDirty(false);
      toast.success('Template reset to default');
    } catch (err) {
      toast.error('Failed to reset template');
    } finally {
      setResetting(false);
    }
  };

  const handlePreview = async () => {
    // Save first if dirty
    if (dirty) {
      setSaving(true);
      try {
        await axios.put(
          `${API}/api/admin/email-templates/${template.type_key}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDirty(false);
      } catch {
        toast.error('Save failed, preview may use old content');
      } finally {
        setSaving(false);
      }
    }
    setLoadingPreview(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/email-templates/${template.type_key}/preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewHtml(res.data.preview_html);
      setShowPreview(true);
    } catch {
      toast.error('Failed to generate preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleTestSend = async () => {
    if (!testEmail) return;
    // Save first if dirty
    if (dirty) {
      try {
        await axios.put(
          `${API}/api/admin/email-templates/${template.type_key}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDirty(false);
      } catch {
        toast.error('Save failed');
        return;
      }
    }
    setSendingTest(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/email-templates/${template.type_key}/test-send`,
        { recipient_email: testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.sent) {
        toast.success(`Test email sent to ${testEmail} (${res.data.mode} mode)`);
      } else {
        toast.error(res.data.error || 'Failed to send');
      }
      setShowTestSend(false);
    } catch (err) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-list-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2 flex-1">
          <TypeIcon typeKey={template.type_key} />
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{template.label}</h2>
            <p className="text-xs text-slate-500">{template.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={handleReset} disabled={resetting}
            data-testid="reset-template-btn"
          >
            {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />}
            Reset
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={handlePreview} disabled={loadingPreview}
            data-testid="preview-template-btn"
          >
            {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            Preview
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => setShowTestSend(true)}
            data-testid="test-send-btn"
          >
            <Send className="h-4 w-4 mr-1" /> Test Send
          </Button>
          <Button
            size="sm" onClick={handleSave}
            disabled={saving || !dirty}
            data-testid="save-template-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Editor */}
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Mail className="h-4 w-4" /> Subject &amp; Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Subject Line</Label>
                <Input
                  data-testid="template-subject-input"
                  value={form.subject}
                  onChange={e => update('subject', e.target.value)}
                  placeholder="Email subject..."
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Preview Text <span className="text-slate-400">(shown in inbox before opening)</span></Label>
                <Input
                  data-testid="template-preview-text-input"
                  value={form.preview_text}
                  onChange={e => update('preview_text', e.target.value)}
                  placeholder="Brief preview..."
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Body Content
                </CardTitle>
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                  <button
                    type="button"
                    data-testid="visual-mode-btn"
                    onClick={() => setHtmlMode(false)}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      !htmlMode
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Type className="h-3 w-3 inline mr-1" />Visual
                  </button>
                  <button
                    type="button"
                    data-testid="html-mode-btn"
                    onClick={() => setHtmlMode(true)}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      htmlMode
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Code className="h-3 w-3 inline mr-1" />HTML
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Heading</Label>
                <Input
                  data-testid="template-heading-input"
                  value={form.heading}
                  onChange={e => update('heading', e.target.value)}
                  placeholder="Optional heading..."
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">
                  Body {htmlMode ? '(HTML Source)' : '(Visual Editor)'}
                </Label>
                {htmlMode ? (
                  <>
                    <Textarea
                      data-testid="template-body-input"
                      value={form.body_html}
                      onChange={e => update('body_html', e.target.value)}
                      placeholder="<p>Hello {{customer_name}}...</p>"
                      className="font-mono text-sm min-h-[300px]"
                      rows={14}
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Raw HTML mode. Use <code className="bg-slate-100 px-1 rounded">{'{{variable}}'}</code> placeholders.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="border rounded-md overflow-hidden" data-testid="template-body-editor">
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={form.body_html}
                        onChange={(val) => update('body_html', val)}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Start writing your email content..."
                        style={{ minHeight: '240px' }}
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Click a variable on the right to insert it at your cursor position. Switch to HTML mode for raw editing.
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Code className="h-4 w-4" /> Call-to-Action &amp; Footer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Button Text</Label>
                  <Input
                    data-testid="template-cta-text-input"
                    value={form.cta_text}
                    onChange={e => update('cta_text', e.target.value)}
                    placeholder="e.g. View Order"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Button URL Variable</Label>
                  <select
                    data-testid="template-cta-url-select"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
                    value={form.cta_url_variable}
                    onChange={e => update('cta_url_variable', e.target.value)}
                  >
                    <option value="">None</option>
                    {ctaVariables.map(v => (
                      <option key={v.key} value={v.key}>{v.label} ({`{{${v.key}}}`})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Template Footer <span className="text-slate-400">(shown above global footer)</span></Label>
                <Input
                  data-testid="template-footer-input"
                  value={form.footer_content}
                  onChange={e => update('footer_content', e.target.value)}
                  placeholder="Optional template-specific footer text..."
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar — Variables */}
        <div className="space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Variable className="h-4 w-4" /> Available Variables
              </CardTitle>
              <CardDescription className="text-xs">Click to copy a variable tag to your clipboard.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {variables.map(v => (
                  <VariableChip key={v.key} variable={v} onInsert={insertVariableIntoEditor} />
                ))}
              </div>
              {variables.length > 0 && (
                <div className="mt-4 border-t pt-3">
                  <p className="text-xs font-medium text-slate-500 mb-2">Sample Values</p>
                  <div className="space-y-1.5">
                    {variables.map(v => (
                      <div key={v.key} className="flex justify-between text-xs">
                        <span className="font-mono text-slate-500">{v.key}</span>
                        <span className="text-slate-700 truncate ml-2 max-w-[140px]">{v.sample}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-slate-50 border-dashed">
            <CardContent className="py-4">
              <p className="text-xs text-slate-500 leading-relaxed">
                <strong>Tip:</strong> The branding wrapper (logo, colors, global footer) is applied automatically from your
                {' '}<span className="font-medium">Email Branding</span> settings. You only edit the inner content here.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-4 w-4" /> Email Preview
            </DialogTitle>
            <DialogDescription>Preview with sample data and current branding settings.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto border rounded-lg bg-slate-100 p-2">
            <iframe
              title="Email Preview"
              srcDoc={previewHtml}
              className="w-full bg-white rounded"
              style={{ minHeight: '500px', border: 'none' }}
              sandbox=""
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Send Dialog */}
      <Dialog open={showTestSend} onOpenChange={setShowTestSend}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-4 w-4" /> Send Test Email
            </DialogTitle>
            <DialogDescription>
              Sends this template with sample data to the specified email address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-sm">Recipient Email</Label>
            <Input
              data-testid="test-send-email-input"
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestSend(false)}>Cancel</Button>
            <Button
              onClick={handleTestSend}
              disabled={sendingTest || !testEmail}
              data-testid="confirm-test-send-btn"
            >
              {sendingTest ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Embeddable Panel (for System Settings integration)                 */
/* ------------------------------------------------------------------ */

export function EmailTemplatesPanel() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const token = localStorage.getItem('token');
  const isSuperAdmin = user?.role === 'super_admin';

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/email-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(res.data.templates || []);
    } catch (err) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isSuperAdmin) fetchTemplates();
    else setLoading(false);
  }, [isSuperAdmin, fetchTemplates]);

  if (!isSuperAdmin) {
    return <p className="text-slate-500 text-center py-8">Super-admin access required.</p>;
  }

  return (
    <div data-testid="email-templates-panel">
      {!selected ? (
        <>
          <div className="mb-4">
            <p className="text-sm text-slate-500">
              Customise the content of every system email. Branding is applied automatically from the Branding tab.
            </p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <TemplateList templates={templates} onSelect={(t) => setSelected(t)} />
          )}
        </>
      ) : (
        <TemplateEditor
          template={selected}
          token={token}
          onBack={() => { setSelected(null); fetchTemplates(); }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Standalone Page (kept for direct route access / backwards compat)  */
/* ------------------------------------------------------------------ */

export default function EmailTemplatesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const token = localStorage.getItem('token');
  const isSuperAdmin = user?.role === 'super_admin';

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/email-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(res.data.templates || []);
    } catch (err) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isSuperAdmin) fetchTemplates();
    else setLoading(false);
  }, [isSuperAdmin, fetchTemplates]);

  if (!isSuperAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12 text-center">
        <p className="text-slate-500">Super-admin access required.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="email-templates-page">
      {!selected ? (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Email Templates</h1>
              <p className="text-sm text-slate-500 mt-1">
                Customise the content of every system email. Branding (logo, colors, footer) is applied automatically.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/settings')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> System Settings
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <TemplateList
              templates={templates}
              onSelect={(t) => setSelected(t)}
            />
          )}
        </>
      ) : (
        <TemplateEditor
          template={selected}
          token={token}
          onBack={() => { setSelected(null); fetchTemplates(); }}
        />
      )}
    </div>
  );
}
